import { z } from "zod";
import {
  normalizeApiBaseUrl,
  parseErrorResponse,
  type FetchLike,
} from "./shared";

export const appVariantSchema = z.enum(["mobile", "desktop", "native"]);

// Token format: Expo push tokens start with "ExponentPushToken[",
// APNs device tokens are 64-char hex strings. Validate both.
const deviceTokenStringSchema = z
  .string()
  .min(1)
  .refine((t) => t.startsWith("ExponentPushToken[") || /^[a-f0-9]+$/i.test(t), {
    message: "Token must be an Expo push token or hex APNs device token",
  });

export const registerDeviceTokenBodySchema = z.object({
  token: deviceTokenStringSchema,
  platform: z.string().min(1),
  app_variant: appVariantSchema.optional(),
});

export const unregisterDeviceTokenBodySchema = z.object({
  token: deviceTokenStringSchema,
});

export function deviceTokensPath(): string {
  return "/api/device-tokens";
}

export async function registerDeviceTokenRequest({
  apiBaseUrl,
  accessToken,
  token,
  platform,
  appVariant,
  fetchImpl,
}: {
  apiBaseUrl: string;
  accessToken: string;
  token: string;
  platform: string;
  appVariant?: string;
  fetchImpl?: FetchLike;
}): Promise<void> {
  const fetchFn = fetchImpl ?? fetch;
  const body = registerDeviceTokenBodySchema.parse({
    token,
    platform,
    ...(appVariant ? { app_variant: appVariant } : {}),
  });

  const response = await fetchFn(
    `${normalizeApiBaseUrl(apiBaseUrl)}${deviceTokensPath()}`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify(body),
    },
  );

  if (!response.ok) {
    throw await parseErrorResponse(response, "Failed to register device token");
  }
}

export async function unregisterDeviceTokenRequest({
  apiBaseUrl,
  accessToken,
  token,
  fetchImpl,
}: {
  apiBaseUrl: string;
  accessToken: string;
  token: string;
  fetchImpl?: FetchLike;
}): Promise<void> {
  const fetchFn = fetchImpl ?? fetch;
  const body = unregisterDeviceTokenBodySchema.parse({ token });

  const response = await fetchFn(
    `${normalizeApiBaseUrl(apiBaseUrl)}${deviceTokensPath()}`,
    {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify(body),
    },
  );

  if (!response.ok) {
    throw await parseErrorResponse(
      response,
      "Failed to unregister device token",
    );
  }
}
